import { useCallback, useEffect, useState } from 'react';
import { useGetPreprocessedAnalyticsMutation } from '../../../../queries/useGetPreprocessedAnalyticsMutation';
import useAuthStore from '../../../../store/authStore';
import useHomeStore from '../../../../store/homeStore';
import {
  DEFAULT_ANALYTICS,
  DEFAULT_VALIDATION,
} from '../../../../types/analytics';
import { IPreprocessedAnalyticsPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { ITEM, TabKey } from '../../../../utils/common';
import { useSignalR } from '../../../../utils/hooks';
import {
  EVENT_ANALYTICS_ACTIVATED,
  EVENT_VALIDATION_ACTIVATED,
  trackUpdate,
} from '../../../../utils/matomoEvents';

interface PopupState {
  [key: string]: {
    key: TabKey;
    visible: boolean;
  };
}

export const usePopupManagement = (
  activePoint: MarkerItem | null | undefined,
  onChangeActiveKey: (key: string | string[], tabKey?: TabKey) => void
) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setShowLogin = useHomeStore((state) => state.setShowLogin);

  const [showPopup, setShowPopup] = useState<PopupState>({});
  const [datasetHasNoAnalyticsVisible, setDatasetHasNoAnalyticsVisible] =
    useState<boolean>(false);
  const [datasetIsOfflineWarningVisible, setDatasetIsOfflineWarningVisible] =
    useState<boolean>(false);
  const [datasetOfflineText, setDatasetOfflineText] = useState<string>('');
  const [datasetNoReportVisible, setDatasetNoReportVisible] =
    useState<boolean>(false);
  const [isReportsVisible, setIsReportsVisible] = useState<boolean>(false);
  const [activePlotValidation, setActivePlotValidation] = useState<string[]>([
    'Altimeter Comparison',
  ]);
  const [updating, setUpdating] = useState<boolean>(false);
  const [links, setLinks] = useState<any[]>([]);
  const [error, setError] = useState<string>('');

  const analyticsMutation = useGetPreprocessedAnalyticsMutation({
    onSuccess: (data) => {
      if (data && data.length > 0) {
        setLinks(data);
        setError('');
      } else {
        setLinks([]);
        setError('No preprocessed data available');
      }
    },
    onError: (error) => {
      setLinks([]);
      setError(error.message || 'Failed to fetch preprocessed analytics');
    },
  });
  const { messageReceived, receivedMessageText, resetMessageState } =
    useSignalR('AnalyticsReceiveMessage', 'JobResult');

  const onUpdatePreprocessedDataLink = useCallback(
    async (payload: IPreprocessedAnalyticsPayload): Promise<void> => {
      try {
        setUpdating(true);
        await analyticsMutation.mutateAsync(payload);
      } finally {
        setUpdating(false);
      }
    },
    [analyticsMutation.mutateAsync]
  );

  useEffect(() => {
    if (messageReceived) {
      const hubMessageParts = receivedMessageText.split(':');
      let result: string;

      if (hubMessageParts.length > 1) {
        result = hubMessageParts[hubMessageParts.length - 1];
      } else {
        result = receivedMessageText;
      }

      if (result && result.toLowerCase() !== 'success') {
        setError(result);
      }

      setUpdating(false);
      resetMessageState();
    }
  }, [messageReceived, receivedMessageText, resetMessageState]);

  const onChangePoint = useCallback(
    (id: string | string[], tabKey: TabKey) => {
      setShowPopup((prevState) => {
        const currentPopup = prevState?.[id as string];

        return {
          ...prevState,
          [id as string]: {
            ...currentPopup,
            key: tabKey,
          },
        };
      });
      onChangeActiveKey(id);
    },
    [activePoint]
  );

  const handleVisibleNewPopup = useCallback(
    (value: boolean): void => {
      if (activePoint) {
        setShowPopup((prevState) => ({
          ...prevState,
          [activePoint.id]: {
            ...(prevState?.[activePoint.id] || { key: ITEM.VALIDATION }),
            visible: value,
          },
        }));
      }
    },
    [activePoint]
  );

  const switchVisiblePopup = useCallback((id: string): void => {
    setShowPopup((prevState) => {
      const newState = { ...prevState };

      Object.keys(newState).forEach((pointId) => {
        newState[pointId] = {
          ...newState[pointId],
          visible: false,
        };
      });

      newState[id] = {
        ...(newState[id] || { key: ITEM.VALIDATION }),
        visible: true,
      };

      return newState;
    });
  }, []);

  const handlePopupKey = useCallback(
    (newKey: TabKey): void => {
      if (activePoint) {
        setShowPopup((prevState) => ({
          ...prevState,
          [activePoint.id]: {
            ...(prevState?.[activePoint.id] || { visible: true }),
            key: newKey,
          },
        }));
      }
    },
    [activePoint]
  );

  const showNewPopup = useCallback(
    (key: TabKey, isDatasetOffline: boolean): void => {
      if (
        (key === ITEM.VALIDATION || key === ITEM.REPORTS) &&
        !isAuthenticated()
      ) {
        setShowLogin(true);
        return;
      }

      if (
        key === ITEM.ANALYTICS &&
        activePoint?.dataset.toLowerCase().includes('nora3_heights')
      ) {
        setDatasetHasNoAnalyticsVisible(true);
        return;
      }

      if (!isDatasetOffline && activePoint) {
        if (activePoint) {
          setShowPopup((prevState) => ({
            ...prevState,
            [activePoint.id]: {
              visible: true,
              key: key,
            },
          }));
        }

        const coordinates = activePoint?.coordinates || [];
        const datasetId = activePoint?.dataset;

        if (coordinates.length && datasetId && key === 'analytics') {
          trackUpdate(
            EVENT_ANALYTICS_ACTIVATED,
            coordinates,
            datasetId,
            DEFAULT_ANALYTICS
          );
        } else if (coordinates.length && datasetId && key === 'validation') {
          trackUpdate(
            EVENT_VALIDATION_ACTIVATED,
            coordinates,
            datasetId,
            DEFAULT_VALIDATION
          );
        }
      } else {
        setDatasetIsOfflineWarningVisible(true);
      }
    },
    [activePoint, isAuthenticated, setShowLogin]
  );

  return {
    showPopup,
    onChangePoint,
    datasetIsOfflineWarningVisible,
    datasetOfflineText,
    datasetNoReportVisible,
    isReportsVisible,
    activePlotValidation,
    updating,
    links,
    error,
    setDatasetOfflineText,
    datasetHasNoAnalyticsVisible,
    setDatasetHasNoAnalyticsVisible,
    setDatasetIsOfflineWarningVisible,
    setDatasetNoReportVisible,
    setIsReportsVisible,
    handleVisibleNewPopup,
    switchVisiblePopup,
    handlePopupKey,
    showNewPopup,
    onUpdatePreprocessedDataLink,
    setActivePlotValidation,
  };
};
